// src/modules/DocumentData.jsx
// Complete document/text data science pipeline
// Upload text/doc → stats → word freq → readability → sentiment → NER → keywords → topics → summary → compare

import React, { useState, useRef, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, ScatterChart, Scatter,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import {
  tokenize, tokenizeSentences,
  getBasicStats, getWordFrequency, getBigrams, getTrigrams,
  getReadabilityScores, getSentiment, extractEntities, extractKeywords,
  extractTopics, extractiveSummary, getCharAnalysis, getWordLengthDist,
  getSentenceLengthDist, getDocumentFingerprint, getPOSTags,
  getLinguisticDiversity, getRepetitionAnalysis, computeSimilarity,
  DEMO_TEXTS,
} from '../../utils/textProcessing.js';
import { Panel, Metric, Tag, Spinner, InfoBox, Tabs, LogPanel, timestamp } from '../../components/ui.jsx';

const PALETTE = ['var(--cyan)', 'var(--green)', 'var(--violet)', 'var(--gold)', 'var(--red)', 'var(--orange)', 'var(--blue)', '#f472b6'];

const DOC_TABS = [
  { id: 'upload',       label: 'INPUT',       icon: '⬆' },
  { id: 'stats',        label: 'STATS',       icon: '◈', requiresData: true },
  { id: 'frequency',    label: 'FREQUENCY',   icon: '⬟', requiresData: true },
  { id: 'readability',  label: 'READABILITY', icon: '◎', requiresData: true },
  { id: 'sentiment',    label: 'SENTIMENT',   icon: '◐', requiresData: true },
  { id: 'ner',          label: 'ENTITIES',    icon: '⬡', requiresData: true },
  { id: 'keywords',     label: 'KEYWORDS',    icon: '◉', requiresData: true },
  { id: 'topics',       label: 'TOPICS',      icon: '⬢', requiresData: true },
  { id: 'summary',      label: 'SUMMARY',     icon: '◫', requiresData: true },
  { id: 'compare',      label: 'COMPARE',     icon: '⊞', requiresData: true },
];

export default function DocumentData({ mode }) {
  const [tab, setTab] = useState('upload');
  const [text, setText] = useState('');
  const [docName, setDocName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [logs, setLogs] = useState([{ ts: '00:00:00', msg: 'Document Data Lab ready.', t: 'info' }]);

  // Analysis results
  const [stats, setStats] = useState(null);
  const [wordFreq, setWordFreq] = useState([]);
  const [bigrams, setBigrams] = useState([]);
  const [trigrams, setTrigrams] = useState([]);
  const [readability, setReadability] = useState(null);
  const [sentiment, setSentiment] = useState(null);
  const [entities, setEntities] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [topics, setTopics] = useState([]);
  const [summary, setSummary] = useState(null);
  const [charAnalysis, setCharAnalysis] = useState(null);
  const [wordLengths, setWordLengths] = useState([]);
  const [sentLengths, setSentLengths] = useState([]);
  const [fingerprint, setFingerprint] = useState(null);
  const [posTags, setPosTags] = useState(null);
  const [diversity, setDiversity] = useState(null);
  const [repetition, setRepetition] = useState(null);

  // Freq tab
  const [freqView, setFreqView] = useState('words'); // words | bigrams | trigrams | chars
  const [showStopwords, setShowStopwords] = useState(false);
  const [freqN, setFreqN] = useState(30);

  // Sentiment tab
  const [sentView, setSentView] = useState('overview'); // overview | sentences | timeline

  // Compare tab
  const [compareText, setCompareText] = useState('');
  const [comparison, setComparison] = useState(null);

  // Summary controls
  const [summaryN, setSummaryN] = useState(4);

  const fileRef = useRef();
  const log = (msg, t = 'info') => setLogs(l => [...l.slice(-60), { ts: timestamp(), msg, t }]);

  const analyze = useCallback((inputText, name) => {
    if (!inputText.trim()) return;
    setProcessing(true);
    log(`Analyzing "${name}"... (${inputText.length} chars)`);

    setTimeout(() => {
      const wf = getWordFrequency(inputText, 50, true);
      const wfAll = getWordFrequency(inputText, 50, false);
      const bg = getBigrams(inputText, 30);
      const tg = getTrigrams(inputText, 20);
      const rd = getReadabilityScores(inputText);
      const sent = getSentiment(inputText);
      const ents = extractEntities(inputText);
      const kw = extractKeywords(inputText, 25);
      const tops = extractTopics(inputText, 5, 8);
      const sum = extractiveSummary(inputText, 5);
      const ch = getCharAnalysis(inputText);
      const wl = getWordLengthDist(inputText);
      const sl = getSentenceLengthDist(inputText);
      const fp = getDocumentFingerprint(inputText);
      const pos = getPOSTags(inputText);
      const div = getLinguisticDiversity(inputText);
      const rep = getRepetitionAnalysis(inputText);
      const bs = getBasicStats(inputText);

      setText(inputText);
      setDocName(name);
      setStats(bs);
      setWordFreq(wf);
      setBigrams(bg);
      setTrigrams(tg);
      setReadability(rd);
      setSentiment(sent);
      setEntities(ents);
      setKeywords(kw);
      setTopics(tops);
      setSummary(sum);
      setCharAnalysis(ch);
      setWordLengths(wl);
      setSentLengths(sl);
      setFingerprint(fp);
      setPosTags(pos);
      setDiversity(div);
      setRepetition(rep);

      log(`✓ ${bs.words} words, ${bs.sentences} sentences, ${bs.uniqueWords} unique`, 'ok');
      log(`Readability: ${rd.fleschLabel} (grade ${rd.fkGrade})`, 'ok');
      log(`Sentiment: ${sent.label} (score ${sent.overallScore})`, 'ok');
      log(`Keywords: ${kw.slice(0, 5).map(k => k.word).join(', ')}`, 'ok');
      setTab('stats');
      setProcessing(false);
    }, 200);
  }, []);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => analyze(e.target.result, file.name);
    reader.readAsText(file);
  };

  const handleCompare = () => {
    if (!text || !compareText.trim()) return;
    const sim = computeSimilarity(text, compareText);
    setComparison(sim);
    log(`Comparison: cosine=${sim.cosine}, jaccard=${sim.jaccard}`, 'ok');
  };

  // ── Derived ──
  const wfDisplay = showStopwords ? getWordFrequency(text, freqN, false) : wordFreq.slice(0, freqN);

  return (
    <div className="anim-fadeup" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <Tabs tabs={DOC_TABS} active={tab} onChange={setTab} dataset={text} />

      {/* ══ INPUT ══ */}
      {tab === 'upload' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          {/* Upload area */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); }}
              onClick={() => fileRef.current?.click()}
              style={{ border: `2px dashed ${isDragging ? 'var(--green)' : 'var(--b1)'}`, borderRadius: '4px', padding: '28px 20px', textAlign: 'center', cursor: 'pointer', background: isDragging ? 'var(--ggreen)' : 'var(--surface)', transition: 'all .2s' }}>
              <input ref={fileRef} type="file" accept=".txt,.md,.csv,.json,.html,.xml,.log,.py,.js,.ts,.jsx,.tsx,.java,.cpp,.c,.cs,.go,.rs,.rb,.php,.sh,.yaml,.yml" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
              {processing
                ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                    <Spinner size={22} /><span style={{ color: 'var(--green)', fontSize: '12px' }}>ANALYZING...</span>
                  </div>
                : <>
                    <div style={{ fontSize: '32px', opacity: .4, marginBottom: '10px' }}>⬢</div>
                    <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '16px', marginBottom: '5px' }}>Drop Text File Here</div>
                    <div style={{ fontSize: '11px', color: 'var(--t3)' }}>.txt .md .json .log .py .js .csv .html and more</div>
                  </>}
            </div>

            {/* Paste area */}
            <div style={{ position: 'relative' }}>
              <textarea
                placeholder="Or paste your text here directly..."
                rows={10}
                style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--b0)', color: 'var(--t1)', padding: '12px 14px', fontSize: '12px', fontFamily: 'JetBrains Mono', lineHeight: 1.6, borderRadius: '3px', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
                onChange={e => {
                  const v = e.target.value;
                  if (v.length > 20) analyze(v, 'pasted-text');
                }}
              />
              <div style={{ fontSize: '10px', color: 'var(--t3)', marginTop: '4px' }}>Analysis triggers automatically after 20 characters</div>
            </div>
          </div>

          {/* Sample texts */}
          <Panel title="SAMPLE DOCUMENTS" accent="var(--green)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '12px' }}>Load a sample to explore all analysis tools:</div>
            {Object.entries(DEMO_TEXTS).map(([name, content]) => (
              <div key={name} onClick={() => analyze(content, name)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', border: '1px solid var(--b0)', borderRadius: '3px', marginBottom: '6px', cursor: 'pointer', background: 'var(--raised)', transition: 'all .15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.background = 'var(--hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--b0)'; e.currentTarget.style.background = 'var(--raised)'; }}>
                <span style={{ fontSize: '20px', color: 'var(--green)' }}>⬢</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: 'var(--t1)', fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: '10px', color: 'var(--t3)' }}>{content.split(' ').length} words · {tokenizeSentences(content).length} sentences</div>
                </div>
                <span style={{ color: 'var(--green)', fontSize: '12px' }}>→</span>
              </div>
            ))}
            <Panel title="LOG" style={{ marginTop: '12px' }}><LogPanel logs={logs} height={80} /></Panel>
          </Panel>
        </div>
      )}

      {/* ══ STATS ══ */}
      {tab === 'stats' && stats && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Core metrics grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <Metric label="Words" value={stats.words.toLocaleString()} color="var(--cyan)" />
            <Metric label="Unique Words" value={stats.uniqueWords.toLocaleString()} color="var(--green)" sub={`${(stats.lexicalDiversity*100).toFixed(1)}% diversity`} />
            <Metric label="Sentences" value={stats.sentences} color="var(--violet)" sub={`avg ${stats.avgSentLen} words/sent`} />
            <Metric label="Paragraphs" value={stats.paragraphs} color="var(--gold)" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <Metric label="Characters" value={stats.chars.toLocaleString()} color="var(--t2)" />
            <Metric label="Avg Word Length" value={stats.avgWordLen} unit="chars" color="var(--cyan)" />
            <Metric label="Read Time" value={`~${stats.estimatedReadTime}`} unit="min" color="var(--green)" sub="at 200 wpm" />
            <Metric label="Lexical Diversity" value={(stats.lexicalDiversity * 100).toFixed(1)} unit="%" color={stats.lexicalDiversity > 0.7 ? 'var(--green)' : stats.lexicalDiversity > 0.4 ? 'var(--gold)' : 'var(--red)'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
            {/* Word length distribution */}
            <Panel title="WORD LENGTH DISTRIBUTION" accent="var(--cyan)">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={wordLengths.filter(d => d.len <= 15)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey="len" stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'chars', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} />
                  <Tooltip formatter={(v, n) => [v, 'count']} />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                    {wordLengths.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} opacity={0.85} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            {/* Sentence length distribution */}
            <Panel title="SENTENCE LENGTH TREND" accent="var(--violet)">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={sentLengths.slice(0, 50)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey="idx" stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'sentence #', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} />
                  <Tooltip formatter={(v) => [v + ' words', 'length']} />
                  <Area type="monotone" dataKey="words" stroke="var(--violet)" fill="var(--violet)" fillOpacity={0.15} strokeWidth={1.5} />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>

            {/* POS breakdown */}
            {posTags && (
              <Panel title="POS TAG DISTRIBUTION" accent="var(--green)">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={Object.entries(posTags.counts).map(([tag, c]) => ({ tag, count: c }))} layout="vertical">
                    <XAxis type="number" stroke="var(--t3)" tick={{ fontSize: 9 }} />
                    <YAxis type="category" dataKey="tag" stroke="var(--t3)" tick={{ fontSize: 10 }} width={50} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[0, 2, 2, 0]}>
                      {['var(--cyan)', 'var(--green)', 'var(--violet)', 'var(--gold)', 'var(--t2)'].map((c, i) => <Cell key={i} fill={c} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            )}
          </div>

          {/* Linguistic diversity */}
          {diversity && (
            <Panel title="LINGUISTIC DIVERSITY METRICS" accent="var(--green)">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px' }}>
                <Metric label="Type-Token Ratio" value={(diversity.ttr * 100).toFixed(1)} unit="%" color="var(--cyan)" sub="unique/total" />
                <Metric label="MATTR" value={(diversity.mattr * 100).toFixed(1)} unit="%" color="var(--green)" sub="moving window TTR" />
                <Metric label="Hapax Legomena" value={diversity.hapax} color="var(--violet)" sub={`${(diversity.hapaxRatio*100).toFixed(0)}% of vocab`} />
                <Metric label="Yule's K" value={diversity.yulesK} color="var(--gold)" sub="vocab richness" />
                <Metric label="Herdan's C" value={diversity.herdanC} color="var(--red)" sub="log(V)/log(N)" />
                <Metric label="Vocabulary" value={diversity.vocabulary} color="var(--t2)" />
              </div>
            </Panel>
          )}

          {/* TTR curve */}
          {fingerprint?.ttrCurve?.length > 0 && (
            <Panel title="VOCABULARY GROWTH CURVE (Type-Token Ratio)" accent="var(--cyan)">
              <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '8px' }}>TTR decreases as text grows — faster drop = more repetition.</div>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={fingerprint.ttrCurve}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey="pos" stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'word position', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} domain={[0, 1]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="ttr" stroke="var(--cyan)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          )}
        </div>
      )}

      {/* ══ FREQUENCY ══ */}
      {tab === 'frequency' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Controls */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '2px', background: 'var(--deep)', border: '1px solid var(--b0)', borderRadius: '3px', padding: '2px' }}>
              {[['words', 'WORDS'], ['bigrams', 'BIGRAMS'], ['trigrams', 'TRIGRAMS'], ['chars', 'CHARS']].map(([v, l]) => (
                <button key={v} onClick={() => setFreqView(v)} style={{ padding: '6px 13px', border: 'none', cursor: 'pointer', fontSize: '10px', letterSpacing: '.08em', fontFamily: 'JetBrains Mono', background: freqView === v ? 'var(--green)' : 'transparent', color: freqView === v ? 'var(--void)' : 'var(--t2)', fontWeight: freqView === v ? 700 : 400, borderRadius: '2px', transition: 'all .12s' }}>{l}</button>
              ))}
            </div>

            {freqView === 'words' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: 'var(--t2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={showStopwords} onChange={e => setShowStopwords(e.target.checked)} style={{ accentColor: 'var(--green)' }} />
                Show stopwords
              </label>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
              <span style={{ fontSize: '11px', color: 'var(--t3)' }}>Top {freqN}:</span>
              <input type="range" min={10} max={50} step={5} value={freqN} onChange={e => setFreqN(parseInt(e.target.value))} style={{ width: '100px', accentColor: 'var(--green)' }} />
            </div>
          </div>

          {/* Word frequency chart */}
          {freqView === 'words' && (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px' }}>
              <Panel title="WORD FREQUENCY CHART" badge={`top ${freqN}`} accent="var(--green)">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={wfDisplay} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false} />
                    <XAxis type="number" stroke="var(--t3)" tick={{ fontSize: 9 }} />
                    <YAxis type="category" dataKey="word" stroke="var(--t3)" tick={{ fontSize: 10 }} width={90} />
                    <Tooltip formatter={(v, n) => [v, 'count']} />
                    <Bar dataKey="count" radius={[0, 2, 2, 0]}>
                      {wfDisplay.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} opacity={0.85} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>

              <Panel title="ZIPF'S LAW" accent="var(--green)">
                <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '10px' }}>Word rank vs frequency — should follow a power law.</div>
                <ResponsiveContainer width="100%" height={220}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                    <XAxis dataKey="rank" stroke="var(--t3)" tick={{ fontSize: 9 }} name="Rank" label={{ value: 'rank', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                    <YAxis dataKey="count" stroke="var(--t3)" tick={{ fontSize: 9 }} name="Frequency" />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Scatter data={wfDisplay.map((d, i) => ({ rank: i + 1, count: d.count, word: d.word }))} fill="var(--green)" opacity={0.7} />
                  </ScatterChart>
                </ResponsiveContainer>
                <div style={{ fontSize: '10px', color: 'var(--t3)', marginTop: '8px' }}>Zipf's Law: rank × frequency ≈ constant for natural language.</div>
              </Panel>
            </div>
          )}

          {/* Bigrams */}
          {freqView === 'bigrams' && (
            <Panel title="BIGRAMS (2-word phrases)" badge={`${bigrams.length} found`} accent="var(--green)">
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={bigrams.slice(0, freqN)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false} />
                  <XAxis type="number" stroke="var(--t3)" tick={{ fontSize: 9 }} />
                  <YAxis type="category" dataKey="bigram" stroke="var(--t3)" tick={{ fontSize: 10 }} width={160} />
                  <Tooltip />
                  <Bar dataKey="count" fill="var(--cyan)" opacity={0.85} radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          )}

          {/* Trigrams */}
          {freqView === 'trigrams' && (
            <Panel title="TRIGRAMS (3-word phrases)" badge={`${trigrams.length} found`} accent="var(--green)">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={trigrams.slice(0, freqN)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false} />
                  <XAxis type="number" stroke="var(--t3)" tick={{ fontSize: 9 }} />
                  <YAxis type="category" dataKey="trigram" stroke="var(--t3)" tick={{ fontSize: 10 }} width={220} />
                  <Tooltip />
                  <Bar dataKey="count" fill="var(--violet)" opacity={0.85} radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          )}

          {/* Character frequency */}
          {freqView === 'chars' && charAnalysis && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <Panel title="LETTER FREQUENCY" badge="A-Z" accent="var(--green)">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={charAnalysis.charFreq}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                    <XAxis dataKey="char" stroke="var(--t3)" tick={{ fontSize: 10 }} />
                    <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="var(--green)" opacity={0.85} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="CHARACTER COMPOSITION" accent="var(--green)">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                  {[
                    ['Lowercase', charAnalysis.lowercase, 'var(--cyan)'],
                    ['Uppercase', charAnalysis.uppercase, 'var(--gold)'],
                    ['Digits', charAnalysis.digits, 'var(--green)'],
                    ['Punctuation', charAnalysis.punctuation, 'var(--violet)'],
                    ['Spaces', charAnalysis.spaces, 'var(--t2)'],
                    ['Vowels', charAnalysis.vowels, 'var(--red)'],
                    ['Consonants', charAnalysis.consonants, 'var(--orange)'],
                    ['Shannon Entropy', charAnalysis.entropy, 'var(--cyan)'],
                  ].map(([label, val, color]) => (
                    <Metric key={label} label={label} value={typeof val === 'number' ? val.toLocaleString() : val} color={color} />
                  ))}
                </div>
                <InfoBox color="var(--cyan)" icon="◈">
                  Vowel ratio: {charAnalysis.vowelRatio}% — English prose typically 38-42%. Deviations suggest code, jargon, or foreign text.
                </InfoBox>
              </Panel>
            </div>
          )}
        </div>
      )}

      {/* ══ READABILITY ══ */}
      {tab === 'readability' && readability && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Main scores */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <Metric label="Flesch Reading Ease" value={readability.flesch} sub={readability.fleschLabel} color={readability.flesch > 60 ? 'var(--green)' : readability.flesch > 40 ? 'var(--gold)' : 'var(--red)'} />
            <Metric label="FK Grade Level" value={readability.fkGrade} sub={readability.fkLabel} color="var(--cyan)" />
            <Metric label="Gunning Fog" value={readability.fog} sub={readability.fogLabel} color="var(--violet)" />
            <Metric label="SMOG Index" value={readability.smog} color="var(--gold)" sub="~grade level" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <Metric label="Coleman-Liau" value={readability.cli} color="var(--blue)" sub="grade level" />
            <Metric label="ARI" value={readability.ari} color="var(--orange)" sub="Auto Readability" />
            <Metric label="Dale-Chall" value={readability.daleChall} color="var(--red)" sub="hard word ratio" />
            <Metric label="Syllables/Word" value={readability.syllablesPerWord} color="var(--t2)" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            {/* Radar comparison */}
            <Panel title="READABILITY RADAR" accent="var(--violet)">
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={[
                  { metric: 'Flesch', score: Math.min(100, readability.flesch) },
                  { metric: 'FK Grade', score: Math.min(20, readability.fkGrade) * 5 },
                  { metric: 'Fog', score: Math.min(20, readability.fog) * 5 },
                  { metric: 'Lex Diversity', score: (stats?.lexicalDiversity || 0) * 100 },
                  { metric: 'Complex Words', score: readability.complexWordPct },
                  { metric: 'Avg Sent Len', score: Math.min(40, stats?.avgSentLen || 0) * 2.5 },
                ]}>
                  <PolarGrid stroke="var(--b1)" />
                  <PolarAngleAxis dataKey="metric" tick={{ fill: 'var(--t3)', fontSize: 10 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: 'var(--t3)', fontSize: 8 }} />
                  <Radar dataKey="score" stroke="var(--violet)" fill="var(--violet)" fillOpacity={0.2} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </Panel>

            {/* Grade level comparison */}
            <Panel title="GRADE LEVEL COMPARISON" accent="var(--violet)">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[
                  { index: 'FK Grade', value: readability.fkGrade, color: 'var(--cyan)' },
                  { index: 'Fog Index', value: readability.fog, color: 'var(--violet)' },
                  { index: 'SMOG', value: readability.smog, color: 'var(--gold)' },
                  { index: 'Coleman-Liau', value: readability.cli, color: 'var(--blue)' },
                  { index: 'ARI', value: readability.ari, color: 'var(--orange)' },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey="index" stroke="var(--t3)" tick={{ fontSize: 9 }} />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'grade', angle: -90, position: 'insideLeft', fill: 'var(--t3)', fontSize: 9 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                    {['var(--cyan)', 'var(--violet)', 'var(--gold)', 'var(--blue)', 'var(--orange)'].map((c, i) => <Cell key={i} fill={c} opacity={0.85} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '10px' }}>
                <Metric label="Complex Words" value={readability.complexWords} unit="" color="var(--red)" sub={`${readability.complexWordPct}% of total`} />
                <Metric label="Total Syllables" value={readability.syllables.toLocaleString()} color="var(--t2)" />
              </div>
            </Panel>
          </div>

          <InfoBox color="var(--cyan)" icon="◈">
            <strong>Flesch</strong> scores 0-100 — higher = easier. Academic papers ≈ 20-40. News ≈ 60-70. Children's books ≈ 80-90. Grade-level metrics estimate years of education required to understand the text.
          </InfoBox>
        </div>
      )}

      {/* ══ SENTIMENT ══ */}
      {tab === 'sentiment' && sentiment && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Controls */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {[['overview', 'OVERVIEW'], ['sentences', 'SENTENCE SCORES'], ['timeline', 'TIMELINE']].map(([v, l]) => (
              <button key={v} onClick={() => setSentView(v)} style={{ padding: '8px 16px', border: `1px solid ${sentView === v ? 'var(--cyan)' : 'var(--b0)'}`, background: sentView === v ? 'var(--gcyan)' : 'var(--raised)', color: sentView === v ? 'var(--cyan)' : 'var(--t2)', fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', transition: 'all .12s' }}>{l}</button>
            ))}
          </div>

          {sentView === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                <Metric label="Overall Sentiment" value={sentiment.label} color={sentiment.overallScore > 0.15 ? 'var(--green)' : sentiment.overallScore < -0.15 ? 'var(--red)' : 'var(--gold)'} sub={`score: ${sentiment.overallScore}`} />
                <Metric label="Positive Words" value={sentiment.posCount} color="var(--green)" sub={`${sentiment.positiveRatio}%`} />
                <Metric label="Negative Words" value={sentiment.negCount} color="var(--red)" sub={`${sentiment.negativeRatio}%`} />
                <Metric label="Intensifiers" value={sentiment.intensifierCount} color="var(--gold)" sub={`${sentiment.negatorCount} negators`} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <Metric label="Positive Sentences" value={sentiment.positiveSentences} color="var(--green)" />
                <Metric label="Negative Sentences" value={sentiment.negativeSentences} color="var(--red)" />
              </div>

              {/* Sentiment breakdown bar */}
              <Panel title="SENTIMENT DISTRIBUTION" accent="var(--cyan)">
                <div style={{ display: 'flex', height: '32px', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
                  {[
                    { val: sentiment.positiveSentences, color: 'var(--green)', label: 'Positive' },
                    { val: sentiment.neutralSentences, color: 'var(--t3)', label: 'Neutral' },
                    { val: sentiment.negativeSentences, color: 'var(--red)', label: 'Negative' },
                  ].map(({ val, color, label }) => {
                    const total = sentiment.sentenceScores.length;
                    const pct = total > 0 ? (val / total) * 100 : 0;
                    return pct > 0 ? (
                      <div key={label} style={{ width: `${pct}%`, background: color, opacity: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {pct > 8 && <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--void)', whiteSpace: 'nowrap' }}>{label} {pct.toFixed(0)}%</span>}
                      </div>
                    ) : null;
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '18px', fontSize: '11px' }}>
                  {[['var(--green)', 'Positive'], ['var(--t3)', 'Neutral'], ['var(--red)', 'Negative']].map(([c, l]) => (
                    <span key={l} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><div style={{ width: '8px', height: '8px', background: c, borderRadius: '2px' }} />{l}</span>
                  ))}
                </div>
              </Panel>
            </div>
          )}

          {sentView === 'sentences' && (
            <Panel title="SENTENCE-LEVEL SENTIMENT" badge={`${sentiment.sentenceScores.length} sentences`} accent="var(--cyan)">
              <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                {sentiment.sentenceScores.map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: '12px', padding: '10px', background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,.1)', borderBottom: '1px solid var(--b0)', alignItems: 'flex-start' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '3px', background: s.label === 'positive' ? 'var(--green)15' : s.label === 'negative' ? 'var(--red)15' : 'var(--b1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '13px', color: s.label === 'positive' ? 'var(--green)' : s.label === 'negative' ? 'var(--red)' : 'var(--t3)' }}>
                        {s.score > 0 ? '+' : ''}{s.score.toFixed(2)}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '11px', color: 'var(--t1)', lineHeight: 1.6 }}>{s.text}</div>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                        <Tag color={s.label === 'positive' ? 'var(--green)' : s.label === 'negative' ? 'var(--red)' : 'var(--t3)'}>{s.label}</Tag>
                        {s.pos > 0 && <Tag color="var(--green)">+{s.pos.toFixed(1)}</Tag>}
                        {s.neg > 0 && <Tag color="var(--red)">-{s.neg.toFixed(1)}</Tag>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {sentView === 'timeline' && (
            <Panel title="SENTIMENT TIMELINE" badge="sentiment arc" accent="var(--cyan)">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={sentiment.sentenceScores.slice(0, 40).map((s, i) => ({ idx: i + 1, score: s.score, label: s.label }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey="idx" stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'sentence #', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} domain={[-1, 1]} />
                  <Tooltip formatter={(v) => [v.toFixed(3), 'sentiment']} />
                  <defs>
                    <linearGradient id="sentGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--green)" stopOpacity={0.3} />
                      <stop offset="50%" stopColor="transparent" stopOpacity={0} />
                      <stop offset="95%" stopColor="var(--red)" stopOpacity={0.3} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="score" stroke="var(--cyan)" fill="url(#sentGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>
          )}
        </div>
      )}

      {/* ══ ENTITIES ══ */}
      {tab === 'ner' && entities && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
            {[
              ['PERSON', 'var(--cyan)', '👤'],
              ['ORG', 'var(--green)', '🏢'],
              ['LOCATION', 'var(--gold)', '📍'],
            ].map(([type, color, icon]) => (
              <Panel key={type} title={type} badge={`${entities[type]?.length || 0} found`} accent={color}>
                {entities[type]?.length > 0
                  ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {entities[type].map(e => (
                        <span key={e} style={{ padding: '4px 10px', background: color + '15', border: `1px solid ${color}44`, fontSize: '11px', color, borderRadius: '2px' }}>{icon} {e}</span>
                      ))}
                    </div>
                  : <span style={{ color: 'var(--t3)', fontSize: '11px' }}>None detected</span>
                }
              </Panel>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
            {[
              ['DATE', 'var(--violet)', '📅'],
              ['MONEY', 'var(--gold)', '💰'],
              ['PERCENT', 'var(--orange)', '📊'],
            ].map(([type, color, icon]) => (
              <Panel key={type} title={type} badge={`${entities[type]?.length || 0} found`} accent={color}>
                {entities[type]?.length > 0
                  ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {entities[type].map(e => (
                        <span key={e} style={{ padding: '4px 10px', background: color + '15', border: `1px solid ${color}44`, fontSize: '11px', color, borderRadius: '2px' }}>{icon} {e}</span>
                      ))}
                    </div>
                  : <span style={{ color: 'var(--t3)', fontSize: '11px' }}>None detected</span>
                }
              </Panel>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            {[
              ['EMAIL', 'var(--cyan)', '✉'],
              ['URL', 'var(--blue)', '🔗'],
            ].map(([type, color, icon]) => (
              <Panel key={type} title={type} badge={`${entities[type]?.length || 0} found`} accent={color}>
                {entities[type]?.length > 0
                  ? entities[type].map(e => <div key={e} style={{ padding: '5px 0', borderBottom: '1px solid var(--b0)', fontSize: '11px', color, wordBreak: 'break-all' }}>{icon} {e}</div>)
                  : <span style={{ color: 'var(--t3)', fontSize: '11px' }}>None detected</span>
                }
              </Panel>
            ))}
          </div>
          <InfoBox color="var(--gold)" icon="⚠">
            NER uses pattern-matching rules, not a trained ML model. Results are approximate. Accuracy improves with formal, well-structured text.
          </InfoBox>
        </div>
      )}

      {/* ══ KEYWORDS ══ */}
      {tab === 'keywords' && keywords.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <Panel title="TF-IDF KEYWORD SCORES" badge={`${keywords.length} keywords`} accent="var(--cyan)">
            <ResponsiveContainer width="100%" height={380}>
              <BarChart data={keywords.slice(0, 20)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false} />
                <XAxis type="number" stroke="var(--t3)" tick={{ fontSize: 9 }} />
                <YAxis type="category" dataKey="word" stroke="var(--t3)" tick={{ fontSize: 10 }} width={100} />
                <Tooltip formatter={(v) => [v, 'TF-IDF']} />
                <Bar dataKey="tfidf" radius={[0, 2, 2, 0]}>
                  {keywords.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} opacity={0.85} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Panel title="KEYWORD CLOUD" accent="var(--cyan)">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', padding: '8px 0' }}>
                {keywords.map((kw, i) => {
                  const maxTf = keywords[0].tfidf;
                  const size = 10 + (kw.tfidf / maxTf) * 14;
                  return (
                    <span key={kw.word} style={{ fontSize: `${size}px`, color: PALETTE[i % PALETTE.length], opacity: 0.7 + (kw.tfidf / maxTf) * 0.3, fontWeight: kw.tfidf > maxTf * 0.6 ? 700 : 400 }}>
                      {kw.word}
                    </span>
                  );
                })}
              </div>
            </Panel>

            <Panel title="KEYWORD TABLE">
              <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead><tr style={{ background: 'var(--deep)' }}>
                    {['Word', 'TF-IDF', 'Freq'].map(h => <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--t3)', fontSize: '10px', borderBottom: '1px solid var(--b0)' }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {keywords.map((kw, i) => (
                      <tr key={kw.word} style={{ borderBottom: '1px solid var(--b0)' }}>
                        <td style={{ padding: '5px 10px', color: PALETTE[i % PALETTE.length], fontWeight: 600 }}>{kw.word}</td>
                        <td style={{ padding: '5px 10px', color: 'var(--t1)' }}>{kw.tfidf}</td>
                        <td style={{ padding: '5px 10px', color: 'var(--t2)' }}>{kw.freq}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ══ TOPICS ══ */}
      {tab === 'topics' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {topics.length === 0
            ? <InfoBox color="var(--gold)" icon="⚠">Need longer text (100+ sentences) for reliable topic modeling. Try a longer document.</InfoBox>
            : <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                  {topics.map((t, ti) => (
                    <div key={ti} style={{ padding: '16px', background: 'var(--surface)', border: `1px solid ${PALETTE[ti % PALETTE.length]}33`, borderRadius: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                        <span style={{ color: PALETTE[ti % PALETTE.length], fontSize: '20px' }}>⬢</span>
                        <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '14px', color: PALETTE[ti % PALETTE.length] }}>{t.topic}</div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {t.words.map((w, wi) => (
                          <span key={w} style={{ padding: '3px 9px', background: PALETTE[ti % PALETTE.length] + '15', border: `1px solid ${PALETTE[ti % PALETTE.length]}44`, fontSize: `${12 - wi * 0.4}px`, color: PALETTE[ti % PALETTE.length], borderRadius: '2px', opacity: 1 - wi * 0.05 }}>
                            {w}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <InfoBox color="var(--cyan)" icon="◈">
                  Topics extracted via co-occurrence clustering. This is a lightweight LDA-lite approach — not a full probabilistic topic model. Results are indicative.
                </InfoBox>
              </>
          }

          {/* Repetition analysis always shown */}
          {repetition && (
            <Panel title="REPETITION & REDUNDANCY ANALYSIS" badge={`${repetition.repeatedPhrases.length} repeated phrases`} accent="var(--orange)">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--t3)', letterSpacing: '.1em', marginBottom: '8px' }}>TOP REPEATED PHRASES</div>
                  {repetition.repeatedPhrases.slice(0, 10).map((p, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--b0)', fontSize: '11px' }}>
                      <span style={{ color: 'var(--t1)', flex: 1 }}>"{p.phrase}"</span>
                      <span style={{ color: 'var(--orange)', fontWeight: 600, marginLeft: '8px' }}>×{p.count}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <Metric label="Redundancy Score" value={`${repetition.redundancyScore}%`} color="var(--orange)" sub="near-duplicate sentences" />
                  {repetition.nearDuplicateSentences.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '10px', color: 'var(--t3)', letterSpacing: '.1em', marginBottom: '8px' }}>NEAR-DUPLICATE SENTENCES</div>
                      {repetition.nearDuplicateSentences.slice(0, 4).map((nd, i) => (
                        <div key={i} style={{ padding: '8px', background: 'var(--raised)', borderRadius: '2px', marginBottom: '6px', border: '1px solid var(--orange)22' }}>
                          <div style={{ fontSize: '10px', color: 'var(--orange)', marginBottom: '4px' }}>sim={nd.similarity} — sentences #{nd.i + 1} and #{nd.j + 1}</div>
                          <div style={{ fontSize: '10px', color: 'var(--t3)' }}>"{nd.s1}"</div>
                          <div style={{ fontSize: '10px', color: 'var(--t3)', marginTop: '2px' }}>"{nd.s2}"</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* ══ SUMMARY ══ */}
      {tab === 'summary' && summary && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '14px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Panel title="EXTRACTIVE SUMMARY" badge={`top ${summaryN} sentences`} accent="var(--violet)">
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px' }}>
                <span style={{ fontSize: '11px', color: 'var(--t3)' }}>Sentences:</span>
                <input type="range" min={1} max={Math.min(10, (tokenizeSentences(text).length || 10))} value={summaryN} onChange={e => setSummaryN(parseInt(e.target.value))} style={{ accentColor: 'var(--violet)', width: '120px' }} />
                <span style={{ fontSize: '11px', color: 'var(--violet)', fontWeight: 600 }}>{summaryN}</span>
              </div>
              {extractiveSummary(text, summaryN).sentences.map((s, i) => (
                <div key={i} style={{ padding: '12px 14px', background: 'var(--raised)', borderLeft: '3px solid var(--violet)', marginBottom: '8px', borderRadius: '0 3px 3px 0' }}>
                  <div style={{ fontSize: '10px', color: 'var(--violet)', marginBottom: '5px', letterSpacing: '.08em' }}>KEY SENTENCE {i + 1}</div>
                  <div style={{ fontSize: '13px', color: 'var(--t1)', lineHeight: 1.65 }}>{s}</div>
                </div>
              ))}
            </Panel>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Panel title="SENTENCE IMPORTANCE SCORES" accent="var(--violet)">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={summary.scores.slice(0, 25)} layout="vertical">
                  <XAxis type="number" stroke="var(--t3)" tick={{ fontSize: 9 }} />
                  <YAxis type="category" dataKey="idx" stroke="var(--t3)" tick={{ fontSize: 9 }} width={35} label={{ value: 'sent#', angle: -90, position: 'insideLeft', fill: 'var(--t3)', fontSize: 8 }} />
                  <Tooltip formatter={(v) => [v, 'importance']} labelFormatter={(l) => `Sentence ${l}`} />
                  <Bar dataKey="score" radius={[0, 2, 2, 0]}>
                    {summary.scores.slice(0, 25).map((s, i) => (
                      <Cell key={i} fill={s.score > 3 ? 'var(--violet)' : s.score > 2 ? 'var(--cyan)' : 'var(--t3)'} opacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="DOCUMENT FINGERPRINT">
              {fingerprint && (
                <div>
                  <div style={{ padding: '10px', background: 'var(--deep)', borderRadius: '2px', marginBottom: '10px', fontFamily: 'JetBrains Mono', fontSize: '14px', letterSpacing: '.1em', color: 'var(--cyan)', textAlign: 'center' }}>
                    {fingerprint.checksum}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    <Metric label="4-char Shingles" value={fingerprint.shingleCount} color="var(--t2)" />
                    <Metric label="Unique Chars" value={charAnalysis?.charFreq.length || 0} color="var(--t2)" />
                  </div>
                </div>
              )}
            </Panel>
          </div>
        </div>
      )}

      {/* ══ COMPARE ══ */}
      {tab === 'compare' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <Panel title="DOCUMENT A (current)" badge={docName} accent="var(--cyan)">
              <div style={{ maxHeight: '160px', overflowY: 'auto', fontSize: '11px', color: 'var(--t2)', lineHeight: 1.6, padding: '4px' }}>
                {text.slice(0, 600)}...
              </div>
              <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Tag color="var(--cyan)">{stats?.words} words</Tag>
                <Tag color="var(--cyan)">{stats?.uniqueWords} unique</Tag>
              </div>
            </Panel>

            <Panel title="DOCUMENT B" badge="paste to compare" accent="var(--violet)">
              <textarea
                value={compareText}
                onChange={e => setCompareText(e.target.value)}
                placeholder="Paste second document here..."
                rows={7}
                style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b0)', color: 'var(--t1)', padding: '10px', fontSize: '11px', fontFamily: 'JetBrains Mono', lineHeight: 1.6, borderRadius: '2px', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
              />
              <button onClick={handleCompare}
                style={{ marginTop: '8px', width: '100%', padding: '10px', background: 'var(--gviolet)', border: '1px solid var(--violet)44', color: 'var(--violet)', fontSize: '11px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', letterSpacing: '.08em', fontWeight: 600 }}>
                COMPARE DOCUMENTS →
              </button>
            </Panel>
          </div>

          {comparison && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <Metric label="Cosine Similarity" value={comparison.cosine} color={comparison.cosine > 0.7 ? 'var(--green)' : comparison.cosine > 0.3 ? 'var(--gold)' : 'var(--red)'} sub={comparison.cosine > 0.7 ? 'Very similar' : comparison.cosine > 0.3 ? 'Somewhat similar' : 'Dissimilar'} />
                <Metric label="Jaccard Index" value={comparison.jaccard} color="var(--cyan)" sub="word overlap" />
                <Metric label="Overlap Coefficient" value={comparison.overlap} color="var(--violet)" sub="relative to smaller doc" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                <Panel title="SHARED VOCABULARY" badge={`${comparison.sharedWords.length} words`} accent="var(--green)">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {comparison.sharedWords.map(w => <span key={w} style={{ padding: '3px 9px', background: 'var(--green)15', border: '1px solid var(--green)33', fontSize: '11px', color: 'var(--green)', borderRadius: '2px' }}>{w}</span>)}
                  </div>
                </Panel>
                <Panel title="UNIQUE TO DOC A" badge={`${comparison.uniqueToDoc1.length} words`} accent="var(--cyan)">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {comparison.uniqueToDoc1.map(w => <span key={w} style={{ padding: '3px 9px', background: 'var(--cyan)15', border: '1px solid var(--cyan)33', fontSize: '11px', color: 'var(--cyan)', borderRadius: '2px' }}>{w}</span>)}
                  </div>
                </Panel>
                <Panel title="UNIQUE TO DOC B" badge={`${comparison.uniqueToDoc2.length} words`} accent="var(--violet)">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {comparison.uniqueToDoc2.map(w => <span key={w} style={{ padding: '3px 9px', background: 'var(--violet)15', border: '1px solid var(--violet)33', fontSize: '11px', color: 'var(--violet)', borderRadius: '2px' }}>{w}</span>)}
                  </div>
                </Panel>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}